import React, { useEffect, useMemo, useState } from 'react';
import { getRecentPageViews } from '../services/pocketbasePageViewTracking';

type PageView = {
  id: string;
  created: string;
  updated: string;
  page: string;
  timestamp: string;
  user_agent: string;
};

export const WebVisitorsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [pageViews, setPageViews] = useState<PageView[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const items = await getRecentPageViews(200);
      if (!cancelled) {
        setPageViews(items as PageView[]);
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalViews = pageViews.length;

  const uniquePages = useMemo(() => {
    return new Set(pageViews.map(v => v.page)).size;
  }, [pageViews]);

  const last24hViews = useMemo(() => {
    const threshold = Date.now() - 24 * 60 * 60 * 1000;
    return pageViews.filter(v => {
      const t = Date.parse(v.timestamp || v.created);
      return Number.isFinite(t) && t >= threshold;
    }).length;
  }, [pageViews]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Web Visitors</h3>
          <p className="text-sm text-gray-600 mt-1">
            Tracks anonymous page opens using the PocketBase <code>page_views</code> collection.
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Total Page Views</div>
                  <div className="text-2xl font-bold text-gray-900">{totalViews}</div>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Last 24 Hours</div>
                  <div className="text-2xl font-bold text-gray-900">{last24hViews}</div>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Unique Pages</div>
                  <div className="text-2xl font-bold text-gray-900">{uniquePages}</div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-900">Recent Page Views</h4>
                  <button
                    type="button"
                    onClick={async () => {
                      setLoading(true);
                      const items = await getRecentPageViews(200);
                      setPageViews(items as PageView[]);
                      setLoading(false);
                    }}
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-200"
                  >
                    Refresh
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pageViews.slice(0, 50).map(view => (
                        <tr key={view.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {new Date(view.timestamp || view.created).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {view.page}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate" title={view.user_agent}>
                            {view.user_agent}
                          </td>
                        </tr>
                      ))}

                      {pageViews.length === 0 && (
                        <tr>
                          <td className="px-6 py-10 text-center text-sm text-gray-500" colSpan={3}>
                            No page views recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
